/**
 * AgentResultCard Component
 *
 * Displays the result from an individual specialist agent
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AgentResult, AGENT_METADATA } from '../../types/superAgent';

interface AgentResultCardProps {
  result: AgentResult;
}

const AgentResultCard: React.FC<AgentResultCardProps> = ({ result }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const metadata = AGENT_METADATA[result.agent_type];

  // Format execution time
  const formatExecutionTime = (ms: number) => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Status configuration
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅' };
      case 'failed':
        return { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌' };
      case 'running':
        return { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '⚙️' };
      case 'pending':
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '⏳' };
      default:
        return { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '•' };
    }
  };

  const statusConfig = getStatusConfig(result.status);

  return (
    <div className={`border rounded-2xl overflow-hidden ${statusConfig.color}`}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-opacity-70 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
              style={{ backgroundColor: metadata.color + '30' }}
            >
              {metadata.icon}
            </div>
            <div>
              <div className="font-semibold text-gray-900">{metadata.name}</div>
              <div className="text-xs text-gray-600">
                {formatExecutionTime(result.execution_time_ms)}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
              <span className="mr-1">{statusConfig.icon}</span>
              {result.status}
            </span>
            <svg
              className={`w-5 h-5 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 bg-white">
          {/* Agent Prompt */}
          {result.agent_prompt && (
            <div className="mb-3">
              <div className="text-xs font-semibold text-gray-700 mb-1">Prompt:</div>
              <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg">
                {result.agent_prompt}
              </div>
            </div>
          )}

          {/* Agent Response */}
          {result.status === 'completed' && result.agent_response && (
            <div>
              <div className="text-xs font-semibold text-gray-700 mb-1">Response:</div>
              <div className="text-sm prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {result.agent_response}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* Error Message */}
          {result.status === 'failed' && result.error_message && (
            <div>
              <div className="text-xs font-semibold text-red-700 mb-1">Error:</div>
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                {result.error_message}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Created: {new Date(result.created_at).toLocaleString()}</span>
              <span>ID: {result.id}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentResultCard;
